import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  MenuItem,
  Select,
  withStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import customInputStyle from '../../assets/jss/material-dashboard-pro-react/components/customInputStyle';

function CustomSelect({
  'data-testid': dataTestId,
  classes,
  error,
  formControlProps,
  helpText,
  IconComponent,
  id,
  inputProps,
  inputRootCustomClasses,
  labelProps,
  labelText,
  multiple,
  name,
  onChange,
  options,
  renderValue,
  required,
  success,
  value,
  white,
}) {
  const parsedOptions = options.map(
    opt => (typeof opt === 'string' ? { value: opt, text: opt } : opt)
  );

  const labelClasses = classNames({
    [` ${classes.labelRootError}`]  : error,
    [` ${classes.labelRootSuccess}`]: success && !error,
  });
  const underlineClasses = classNames({
    [classes.underlineError]  : error,
    [classes.underlineSuccess]: success && !error,
    [classes.underline]       : true,
    [classes.whiteUnderline]  : white,
  });
  const marginTop = classNames({
    [inputRootCustomClasses]: inputRootCustomClasses !== undefined,
  });
  const inputClasses = classNames({
    [classes.input]     : true,
    [classes.whiteInput]: white,
  });
  let formControlClasses;
  if (formControlProps !== undefined) {
    formControlClasses = classNames(formControlProps.className, classes.formControl);
  } else {
    formControlClasses = classes.formControl;
  }
  const helpTextClasses = classNames({
    [classes.labelRootError]  : error,
    [classes.labelRootSuccess]: success && !error,
  });
  return (
    <FormControl {...formControlProps} className={formControlClasses}>
      {labelText !== undefined ? (
        <InputLabel
          error={error}
          required={required}
          data-testid={dataTestId ? `${dataTestId}-label` : undefined}
          className={`${classes.labelRoot} ${labelClasses}`}
          htmlFor={id}
          {...labelProps}
        >
          {labelText}
        </InputLabel>
      ) : null}
      <Select
        data-testid={dataTestId}
        id={id}
        multiple={multiple}
        renderValue={renderValue ? selected => renderValue(selected, parsedOptions) : undefined}
        input={(
          <Input
            error={error}
            onChange={onChange}
            value={value}
            name={name}
            classes={{
              input    : inputClasses,
              root     : marginTop,
              disabled : classes.disabled,
              underline: underlineClasses,
            }}
            id={id}
            {...inputProps}
            inputProps={{ 'data-testid': dataTestId ? `${dataTestId}-input` : undefined }}
          />
        )}
        IconComponent={IconComponent}
      >
        {parsedOptions.map((option, idx) => {
          const properKey = typeof option.value === 'object' ? idx : option.value;
          const properDataTestId = dataTestId
            ? `${dataTestId}-option-${idx}`
            : undefined;
          return (
            <MenuItem
              data-testid={properDataTestId}
              key={properKey}
              value={option.value}
              disabled={option.disabled || false}
            >
              {option.text}
            </MenuItem>
          );
        })}
      </Select>
      {helpText !== undefined ? (
        <FormHelperText id={`${id}-text`} className={helpTextClasses}>
          {helpText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}

CustomSelect.propTypes = {
  'data-testid'         : PropTypes.string,
  classes               : PropTypes.object.isRequired,
  error                 : PropTypes.bool,
  formControlProps      : PropTypes.object,
  helpText              : PropTypes.node,
  IconComponent         : PropTypes.func,
  id                    : PropTypes.string,
  inputProps            : PropTypes.object,
  inputRootCustomClasses: PropTypes.string,
  labelProps            : PropTypes.object,
  labelText             : PropTypes.node.isRequired,
  multiple              : PropTypes.bool,
  name                  : PropTypes.string,
  onChange              : PropTypes.func,
  options               : PropTypes.array,
  renderValue           : PropTypes.func,
  required              : PropTypes.bool,
  success               : PropTypes.bool,
  value                 : PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  white                 : PropTypes.bool,
};

CustomSelect.defaultProps = {
  'data-testid'         : undefined,
  error                 : false,
  formControlProps      : {},
  helpText              : undefined,
  IconComponent         : undefined,
  id                    : undefined,
  inputProps            : {},
  inputRootCustomClasses: undefined,
  labelProps            : {},
  multiple              : false,
  name                  : undefined,
  onChange              : undefined,
  options               : undefined,
  renderValue           : undefined,
  required              : false,
  success               : false,
  value                 : undefined,
  white                 : false,
};

export default withStyles(customInputStyle)(CustomSelect);
